import { byLocale, type AppLocale } from '~/constants/appLocale'
import type { CVData } from '~/types/cv'

/**
 * "Three things I would improve on your CV" for the first newsletter e-mail,
 * and the review the e-mail's button opens on the CV page (LandingCvReview).
 *
 * Plain rules over the CV data, no AI call: cheap enough for every sign-up
 * and predictable enough to put in an e-mail unseen. Each finding names the
 * visitor's own role or numbers, which is what makes the e-mail worth
 * opening; the generic fallbacks only fill up to three.
 *
 * The same findings give the CV a score (cvReviewScore), so the e-mail and
 * the page can say how far the fixes take it.
 *
 * Returns plain text. Anything from the CV must be escaped by the caller.
 */

export interface CvFinding {
  id: string
  title: string
  text: string
}

type Locale = AppLocale

const clip = (value: string | undefined | null, max = 60) => {
  const text = (value || '').replace(/\s+/g, ' ').trim()
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

const roleLabel = (exp: { title?: string, company?: string }, locale: Locale) => {
  const title = clip(exp.title) || byLocale(locale, { cs: 'vaší pozice', en: 'your role', de: 'Ihre Stelle', pl: 'Twoje stanowisko' })
  const company = clip(exp.company, 40)
  return company ? `${title} (${company})` : title
}

const PASSIVE = /\b(zodpov[eě]dn|odpov[eě]dn[aáýy]? za|m[eě]l[a]? jsem na starosti|responsible for|in charge of|duties included|verantwortlich f[üu]r|zust[äa]ndig f[üu]r|zu meinen aufgaben geh[öo]rte|odpowiedzialn[yaei]+ za|do moich obowi[aą]zk[oó]w nale[zż]a[lł]o)/i

/** "1 dovednost", "2 dovednosti", "5 dovedností". */
const czechSkills = (n: number) => n === 1 ? 'dovednost' : n < 5 ? 'dovednosti' : 'dovedností'

/** "1 umiejętność", "2 umiejętności", "5 umiejętności", "22 umiejętności": the same but for one. */
const polishSkills = (n: number) => n === 1 ? 'umiejętność' : 'umiejętności'

/** The three for the e-mail: the CV's own findings first, general advice to fill up. */
export function reviewCv(cv: Partial<CVData> | null | undefined, locale: Locale): CvFinding[] {
  const picked = cvFindings(cv, locale).slice(0, 3)
  return picked.length < 3 ? [...picked, ...fallbackFindings(locale, 3 - picked.length)] : picked
}

/** Every finding the rules make on this CV, most telling first; none for no CV. */
export function cvFindings(cv: Partial<CVData> | null | undefined, locale: Locale): CvFinding[] {
  const findings: CvFinding[] = []
  if (!cv) return findings

  const experiences = (cv.experiences || []).filter(exp => exp && (exp.title || exp.company || exp.description))
  const withText = experiences.filter(exp => (exp.description || '').trim().length > 0)

  const noNumbers = withText.find(exp => !/\d/.test(exp.description))
  if (noNumbers) {
    const role = roleLabel(noNumbers, locale)
    findings.push(byLocale(locale, {
      cs: { id: 'numbers', title: 'Výsledky v číslech', text: `Popis pozice ${role} neobsahuje jediné číslo. Doplňte aspoň jeden výsledek: o kolik procent, kolik zákazníků, lidí nebo peněz, za jak dlouho. Čísla hledá personalista i ATS.` },
      en: { id: 'numbers', title: 'Results in numbers', text: `The description of ${role} has no numbers at all. Add at least one result: by what percentage, how many customers, people or how much money, in what time. Recruiters and ATS both look for numbers.` },
      de: { id: 'numbers', title: 'Ergebnisse in Zahlen', text: `Die Beschreibung der Stelle ${role} enthält keine einzige Zahl. Ergänzen Sie mindestens ein Ergebnis: um wie viel Prozent, wie viele Kunden, Menschen oder wie viel Geld, in welcher Zeit. Personaler und ATS suchen beide nach Zahlen.` },
      pl: { id: 'numbers', title: 'Rezultaty w liczbach', text: `Opis stanowiska ${role} nie zawiera ani jednej liczby. Dodaj przynajmniej jeden rezultat: o ile procent, ilu klientów, osób lub ile pieniędzy, w jakim czasie. Liczb szuka i rekruter, i ATS.` },
    }))
  }

  const passive = withText.find(exp => PASSIVE.test(exp.description))
  if (passive) {
    const role = roleLabel(passive, locale)
    findings.push(byLocale(locale, {
      cs: { id: 'passive', title: 'Čin místo náplně práce', text: `U pozice ${role} popisujete, za co jste odpovídali. Silnější je napsat, co jste udělali a co z toho bylo: „Zavedl jsem…, takže…“ místo „Zodpovědný za…“.` },
      en: { id: 'passive', title: 'Achievements, not duties', text: `For ${role} you describe what you were responsible for. It is stronger to say what you did and what came of it: "Introduced…, which…" instead of "Responsible for…".` },
      de: { id: 'passive', title: 'Erfolge statt Aufgaben', text: `Bei der Stelle ${role} beschreiben Sie, wofür Sie zuständig waren. Stärker ist, zu sagen, was Sie getan haben und was daraus wurde: „Eingeführt…, wodurch…“ statt „Verantwortlich für…“.` },
      pl: { id: 'passive', title: 'Osiągnięcia zamiast obowiązków', text: `Przy stanowisku ${role} opisujesz, za co odpowiadano. Mocniej jest napisać, co zrobiono i co z tego wynikło: „Wdrożyłam…, dzięki czemu…” zamiast „Odpowiedzialny za…”.` },
    }))
  }

  const about = (cv.aboutMe || '').trim()
  if (!about) {
    findings.push(byLocale(locale, {
      cs: { id: 'summary-missing', title: 'Shrnutí nahoře', text: 'CV nemá úvodní shrnutí. Tři věty pod jménem – kdo jste, v čem jste dobří a co hledáte – rozhodují, jestli personalista čte dál.' },
      en: { id: 'summary-missing', title: 'A summary at the top', text: 'Your CV has no summary. Three sentences under your name – who you are, what you are good at and what you are looking for – decide whether a recruiter reads on.' },
      de: { id: 'summary-missing', title: 'Ein Kurzprofil oben', text: 'Ihr Lebenslauf hat kein Kurzprofil. Drei Sätze unter Ihrem Namen – wer Sie sind, was Sie gut können und was Sie suchen – entscheiden, ob ein Personaler weiterliest.' },
      pl: { id: 'summary-missing', title: 'Podsumowanie na górze', text: 'Twoje CV nie ma podsumowania. Trzy zdania pod imieniem i nazwiskiem – kim jesteś, co robisz najlepiej i czego szukasz – decydują, czy rekruter czyta dalej.' },
    }))
  } else if (about.length > 600) {
    findings.push(byLocale(locale, {
      cs: { id: 'summary-long', title: 'Kratší shrnutí', text: `Shrnutí má ${about.length} znaků. Zkraťte ho na 3–4 věty; personalista mu věnuje pár vteřin.` },
      en: { id: 'summary-long', title: 'A shorter summary', text: `Your summary is ${about.length} characters long. Cut it to 3–4 sentences; a recruiter gives it a few seconds.` },
      de: { id: 'summary-long', title: 'Ein kürzeres Kurzprofil', text: `Ihr Kurzprofil hat ${about.length} Zeichen. Kürzen Sie es auf 3–4 Sätze; ein Personaler gibt ihm ein paar Sekunden.` },
      pl: { id: 'summary-long', title: 'Krótsze podsumowanie', text: `Podsumowanie ma ${about.length} znaków. Skróć je do 3–4 zdań; rekruter poświęca mu kilka sekund.` },
    }))
  }

  const technical = (cv.technicalSkills || []).filter(Boolean)
  const skillCount = technical.length + (cv.softSkills || []).filter(Boolean).length
  if (technical.length === 0 && skillCount > 0) {
    findings.push(byLocale(locale, {
      cs: { id: 'skills-tools', title: 'Konkrétní nástroje', text: 'Máte jen obecné dovednosti. Doplňte konkrétní nástroje, programy a metody, které používáte – podle nich ATS páruje CV s inzeráty.' },
      en: { id: 'skills-tools', title: 'Concrete tools', text: 'You list only general skills. Add the concrete tools, software and methods you use – ATS matches CVs to job ads by them.' },
      de: { id: 'skills-tools', title: 'Konkrete Tools', text: 'Sie nennen nur allgemeine Kenntnisse. Ergänzen Sie die konkreten Tools, Programme und Methoden, die Sie nutzen – danach ordnet ein ATS Lebensläufe den Anzeigen zu.' },
      pl: { id: 'skills-tools', title: 'Konkretne narzędzia', text: 'Masz tylko ogólne umiejętności. Dodaj konkretne narzędzia, programy i metody, których używasz – według nich ATS dopasowuje CV do ogłoszeń.' },
    }))
  } else if (skillCount < 6) {
    findings.push(byLocale(locale, {
      cs: { id: 'skills-few', title: 'Víc dovedností', text: `${skillCount === 0 ? 'V CV nemáte žádné dovednosti' : `V CV máte jen ${skillCount} ${czechSkills(skillCount)}`}. Uveďte 6–10 konkrétních, ideálně stejnými slovy jako v inzerátu – podle nich ATS páruje CV s pozicemi.` },
      en: { id: 'skills-few', title: 'More skills', text: `Your CV lists ${skillCount === 0 ? 'no skills' : `only ${skillCount} skill${skillCount === 1 ? '' : 's'}`}. Name 6–10 concrete ones, ideally in the job ad's words – ATS matches CVs to jobs by them.` },
      de: { id: 'skills-few', title: 'Mehr Kenntnisse', text: `Ihr Lebenslauf nennt ${skillCount === 0 ? 'keine Kenntnisse' : `nur ${skillCount === 1 ? 'eine Kenntnis' : `${skillCount} Kenntnisse`}`}. Nennen Sie 6–10 konkrete, am besten in den Worten der Anzeige – danach ordnet ein ATS Lebensläufe den Stellen zu.` },
      pl: { id: 'skills-few', title: 'Więcej umiejętności', text: `${skillCount === 0 ? 'Twoje CV nie wymienia żadnych umiejętności' : `Twoje CV wymienia tylko ${skillCount} ${polishSkills(skillCount)}`}. Podaj 6–10 konkretnych, najlepiej tymi samymi słowami co w ogłoszeniu – według nich ATS dopasowuje CV do stanowisk.` },
    }))
  }

  if (!clip(cv.title)) {
    findings.push(byLocale(locale, {
      cs: { id: 'headline', title: 'Titulek pod jménem', text: 'Pod jménem chybí název pozice, o kterou se ucházíte. Je to první, co personalista hledá, a ATS podle něj CV třídí.' },
      en: { id: 'headline', title: 'A headline under your name', text: 'There is no job title under your name. It is the first thing a recruiter looks for, and ATS sorts CVs by it.' },
      de: { id: 'headline', title: 'Eine Überschrift unter Ihrem Namen', text: 'Unter Ihrem Namen fehlt die Stelle, auf die Sie sich bewerben. Danach sucht ein Personaler zuerst, und ein ATS sortiert Lebensläufe danach.' },
      pl: { id: 'headline', title: 'Nagłówek pod imieniem i nazwiskiem', text: 'Pod imieniem i nazwiskiem brakuje stanowiska, o które się ubiegasz. To pierwsza rzecz, której szuka rekruter, a ATS według niej sortuje CV.' },
    }))
  }

  const long = withText.find(exp => exp.description.length > 900)
  if (long) {
    const role = roleLabel(long, locale)
    findings.push(byLocale(locale, {
      cs: { id: 'long', title: 'Odrážky místo odstavce', text: `Popis pozice ${role} má ${long.description.length} znaků. Rozdělte ho do 3–5 odrážek s tím nejdůležitějším.` },
      en: { id: 'long', title: 'Bullets, not a wall of text', text: `The description of ${role} is ${long.description.length} characters long. Split it into 3–5 bullets with what matters most.` },
      de: { id: 'long', title: 'Stichpunkte statt Textwand', text: `Die Beschreibung der Stelle ${role} hat ${long.description.length} Zeichen. Teilen Sie sie in 3–5 Stichpunkte mit dem Wichtigsten.` },
      pl: { id: 'long', title: 'Punkty zamiast ściany tekstu', text: `Opis stanowiska ${role} ma ${long.description.length} znaków. Podziel go na 3–5 punktów z tym, co najważniejsze.` },
    }))
  }

  const undated = experiences.find(exp => !(exp.startDate || '').trim())
  if (undated) {
    const role = roleLabel(undated, locale)
    findings.push(byLocale(locale, {
      cs: { id: 'dates', title: 'Data u pozic', text: `U pozice ${role} chybí, od kdy do kdy jste tam pracovali. Bez dat působí CV nedůvěryhodně a ATS neumí spočítat praxi.` },
      en: { id: 'dates', title: 'Dates for every role', text: `${role} has no dates. Without them a CV looks less trustworthy and ATS cannot count your experience.` },
      de: { id: 'dates', title: 'Daten bei jeder Stelle', text: `Bei der Stelle ${role} fehlt, von wann bis wann Sie dort gearbeitet haben. Ohne Daten wirkt ein Lebenslauf weniger glaubwürdig, und ein ATS kann Ihre Berufsjahre nicht berechnen.` },
      pl: { id: 'dates', title: 'Daty przy każdym stanowisku', text: `Przy stanowisku ${role} brakuje dat, od kiedy do kiedy tam pracowano. Bez dat CV wygląda mniej wiarygodnie, a ATS nie policzy Twojego doświadczenia.` },
    }))
  }

  if (!cv.contact?.linkedin && !(cv.websites || []).some(site => /linkedin\./i.test(site?.url || ''))) {
    findings.push(byLocale(locale, {
      cs: { id: 'linkedin', title: 'Odkaz na LinkedIn', text: 'V kontaktech chybí LinkedIn. Personalisté si ho otevírají skoro vždy; přidejte odkaz na profil.' },
      en: { id: 'linkedin', title: 'A LinkedIn link', text: 'Your contact details have no LinkedIn. Recruiters almost always open it; add the link to your profile.' },
      de: { id: 'linkedin', title: 'Ein Link zu LinkedIn', text: 'In Ihren Kontaktdaten fehlt LinkedIn. Personaler öffnen es fast immer; fügen Sie den Link zu Ihrem Profil hinzu.' },
      pl: { id: 'linkedin', title: 'Link do LinkedIn', text: 'W danych kontaktowych brakuje LinkedIn. Rekruterzy otwierają go prawie zawsze; dodaj link do swojego profilu.' },
    }))
  }

  return findings
}

/**
 * Points each finding costs. A CV with none scores CV_REVIEW_MAX, not 100:
 * the rest is fitting it to a posting, which no rule over the CV alone can
 * judge (that is the tailoring's score).
 */
const FINDING_WEIGHTS: Record<string, number> = {
  'numbers': 14,
  'summary-missing': 14,
  'skills-few': 10,
  'headline': 10,
  'dates': 10,
  'passive': 8,
  'skills-tools': 8,
  'summary-long': 6,
  'long': 6,
  'linkedin': 5,
}
/** What fixing one finding adds to the score. */
export const findingPoints = (id: string): number => FINDING_WEIGHTS[id] ?? 0

export const CV_REVIEW_MAX = 95
const CV_REVIEW_MIN = 15

export interface CvReviewScore {
  /** The CV as it is. */
  before: number
  /** With every finding fixed. */
  after: number
}

export function cvReviewScore(findings: readonly CvFinding[]): CvReviewScore {
  const lost = findings.reduce((sum, finding) => sum + findingPoints(finding.id), 0)
  return { before: Math.max(CV_REVIEW_MIN, CV_REVIEW_MAX - lost), after: CV_REVIEW_MAX }
}

function fallbackFindings(locale: Locale, count: number): CvFinding[] {
  const all: CvFinding[] = byLocale(locale, {
    cs: [
      { id: 'tailor', title: 'Verze pro každý inzerát', text: 'Před každou přihláškou projděte inzerát a stejná klíčová slova použijte v CV. Jedno CV na všechno projde ATS hůř.' },
      { id: 'order', title: 'To nejdůležitější nahoru', text: 'Poslední a nejrelevantnější pozice patří úplně nahoru, starší role stačí zkrátit na jeden řádek.' },
      { id: 'proofread', title: 'Cizí oči', text: 'Dejte CV přečíst někomu jinému a zeptejte se, co si zapamatoval po 10 vteřinách. Přesně tolik má personalista.' },
    ],
    en: [
      { id: 'tailor', title: 'A version for every job ad', text: 'Before every application go through the ad and use its keywords in your CV. One CV for everything does worse in ATS.' },
      { id: 'order', title: 'What matters most goes first', text: 'Your latest and most relevant role goes right at the top; older roles can shrink to one line.' },
      { id: 'proofread', title: 'Fresh eyes', text: 'Have someone else read your CV and ask what they remember after 10 seconds. That is all a recruiter gives it.' },
    ],
    de: [
      { id: 'tailor', title: 'Eine Version für jede Anzeige', text: 'Gehen Sie vor jeder Bewerbung die Anzeige durch und verwenden Sie ihre Schlüsselwörter im Lebenslauf. Ein Lebenslauf für alles schneidet im ATS schlechter ab.' },
      { id: 'order', title: 'Das Wichtigste nach oben', text: 'Ihre neueste und relevanteste Stelle gehört ganz nach oben; ältere Stellen können auf eine Zeile schrumpfen.' },
      { id: 'proofread', title: 'Ein frischer Blick', text: 'Lassen Sie jemand anderen Ihren Lebenslauf lesen und fragen Sie, was nach 10 Sekunden hängen geblieben ist. Mehr gibt ihm ein Personaler nicht.' },
    ],
    pl: [
      { id: 'tailor', title: 'Wersja na każde ogłoszenie', text: 'Przed każdą aplikacją przejrzyj ogłoszenie i użyj jego słów kluczowych w CV. Jedno CV na wszystko gorzej przechodzi przez ATS.' },
      { id: 'order', title: 'Najważniejsze na górę', text: 'Ostatnie i najbardziej pasujące stanowisko idzie na samą górę; starsze wystarczy skrócić do jednej linijki.' },
      { id: 'proofread', title: 'Świeże spojrzenie', text: 'Daj CV do przeczytania komuś innemu i zapytaj, co zapamiętał po 10 sekundach. Tyle daje mu rekruter.' },
    ],
  })
  return all.slice(0, count)
}

/** First name for a greeting, or null when the CV has none. */
export function firstName(cv: Partial<CVData> | null | undefined): string | null {
  const first = (cv?.name || '').trim().split(/\s+/)[0]
  return first && first.length <= 30 ? first : null
}

/** The role the CV is aimed at, for subject lines. */
export function targetRole(cv: Partial<CVData> | null | undefined): string | null {
  return clip(cv?.title, 50) || null
}

/** The first half of a fix, cut at a word and trailed off: the rest is for the pack (the e-mail and LandingCvReview). */
export function halfText(text: string): string {
  const words = text.split(/\s+/).filter(Boolean)
  if (words.length < 6) return text
  return `${words.slice(0, Math.ceil(words.length / 2)).join(' ').replace(/[,.;:–-]+$/, '')}…`
}
